import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
    Box, 
    Button, 
    Typography, 
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    makeStyles
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { 
    CheckCircle, 
    Description,
    CloudUpload,
    Image as ImageIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import AIButton from './AIButton';
import Currency from '../../../../commonModules/Currency';

const useStyles = makeStyles((theme) => ({
    dropzone: {
        border: '2px dashed #667eea',
        borderRadius: '8px',
        padding: '40px 20px',
        textAlign: 'center',
        backgroundColor: '#f8f9ff',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: '#f0f2ff',
            borderColor: '#5568d3'
        }
    },
    dropzoneActive: {
        backgroundColor: '#e8ebff',
        borderColor: '#5568d3',
        borderStyle: 'solid'
    }
}));

const InvoiceUploader = ({ onExtractComplete, shopSelected = false, matchProduct, PRODUCTS }) => {
    const classes = useStyles();
    
    const [state, setState] = useState({
        file: null,
        preview: null,
        loading: false,
        loadingStep: 0,
        error: null,
        extractedData: null,
        dialogOpen: false,
        uploadDialogOpen: false
    });

    const LOADING_STEPS = [
        "Extraction des données de la facture (IA Mistral)",
        "Correspondance des produits au catalogue (IA)"
    ];

    const handleButtonClick = () => {
        if (!shopSelected) {
            toast.error("Veuillez sélectionner un magasin avant d'importer une facture.");
            return;
        }
        setState(prevState => ({ ...prevState, uploadDialogOpen: true }));
    };

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length === 0) return;
        
        const file = acceptedFiles[0];
        
        // Create preview for images
        let preview = null;
        if (file.type.startsWith('image/')) {
            preview = URL.createObjectURL(file);
        }

        setState(prevState => ({
            ...prevState,
            file: file,
            preview: preview,
            error: null
        }));
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'application/pdf': ['.pdf']
        },
        maxFiles: 1,
        maxSize: 10 * 1024 * 1024, // 10MB
        onDropRejected: (fileRejections) => {
            const rejection = fileRejections[0];
            if (rejection.errors[0].code === 'file-too-large') {
                toast.error("Fichier trop volumineux (max 10MB)");
            } else if (rejection.errors[0].code === 'file-invalid-type') {
                toast.error("Format non supporté. Utilisez JPG, PNG ou PDF");
            } else {
                toast.error("Erreur lors de la sélection du fichier");
            }
        }
    });

    const handleExtract = async () => {
        if (!state.file) {
            toast.error("Veuillez sélectionner une facture");
            return;
        }

        if (!shopSelected) {
            toast.error("Veuillez sélectionner un magasin avant d'importer une facture.");
            return;
        }

        setState(prevState => ({ 
            ...prevState, 
            loading: true, 
            loadingStep: 0,
            error: null,
            uploadDialogOpen: false 
        }));

        try {
            const formData = new FormData();
            formData.append('file', state.file);
            const response = await fetch('/invoice/extract', {
                method: 'POST',
                body: formData,
                credentials: 'same-origin',
            });

            const result = await response.json();

            if (result.success) {
                const data = result.data;
                const extractedProducts = data.products || [];

                // Build catalog for AI matching
                const catalog = PRODUCTS
                    ? Object.values(PRODUCTS)
                        .filter((p) => !p.is_disabled)
                        .map((p) => ({ id: p.id, name: p.name, marque: p.marque || "", ref: p.ref || "" }))
                    : [];

                let aiMatches = {};
                if (extractedProducts.length > 0 && catalog.length > 0) {
                    setState(prev => ({ ...prev, loadingStep: 1 }));
                    try {
                        const matchRes = await fetch("/invoice/match-products", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            credentials: "same-origin",
                            body: JSON.stringify({
                                products: extractedProducts.map((p) => ({
                                    name: p.name,
                                    ref: p.ref,
                                    quantity: p.quantity,
                                    unit_price: p.unit_price,
                                    total_price: p.total_price
                                })),
                                catalog
                            })
                        });
                        const matchData = await matchRes.json();
                        if (matchData.success && matchData.matches) {
                            aiMatches = matchData.matches;
                        }
                    } catch (err) {
                        console.warn("AI product matching failed, using local fallback:", err);
                    }
                }

                const productsWithMatch = extractedProducts.map((p, index) => {
                    const aiMatched = aiMatches[String(index)] && PRODUCTS?.[aiMatches[String(index)].id];
                    const localMatched = matchProduct ? matchProduct(p) : null;
                    const matchedProduct = aiMatched || localMatched;
                    return {
                        ...p,
                        matched: !!matchedProduct,
                        matchedProduct: matchedProduct || null
                    };
                });

                setState(prevState => ({
                    ...prevState,
                    loading: false,
                    extractedData: { ...data, products: productsWithMatch },
                    dialogOpen: true
                }));
                const unmatchedCount = productsWithMatch.filter((p) => !p.matched).length;
                if (unmatchedCount > 0) {
                    toast.warning(`${unmatchedCount} produit(s) non trouvé(s) dans le catalogue - affichés en rouge`);
                } else {
                    toast.success("Extraction réussie!");
                }
            } else {
                throw new Error(result.error || "Extraction failed");
            }

        } catch (error) {
            console.error("Extraction error:", error);
            const errorMsg = error.message || "Erreur lors de l'extraction";
            setState(prevState => ({
                ...prevState,
                loading: false,
                error: errorMsg
            }));
            toast.error(errorMsg);
        }
    };

    const handleConfirm = () => {
        if (state.extractedData && onExtractComplete) {
            onExtractComplete(state.extractedData);
        }
        
        // Clean up and reset
        if (state.preview) {
            URL.revokeObjectURL(state.preview);
        }
        
        setState({
            file: null,
            preview: null,
            loading: false,
            error: null,
            extractedData: null,
            dialogOpen: false,
            uploadDialogOpen: false
        });
        
        toast.success("Données importées dans le formulaire");
    };

    const handleCancel = () => {
        setState(prevState => ({ ...prevState, dialogOpen: false }));
    };

    const handleCancelUpload = () => {
        if (state.preview) {
            URL.revokeObjectURL(state.preview);
        }
        setState(prevState => ({
            ...prevState,
            uploadDialogOpen: false,
            file: null,
            preview: null,
            error: null
        }));
    };

    const invoiceCurrency = state.extractedData?.currency || 'FCFA';
    const sourceCurrency = invoiceCurrency === 'EUR' ? 'Euro' : (invoiceCurrency === 'USD' ? 'USD' : 'FCFA');
    const primaryFcfa = sourceCurrency !== 'FCFA';

    return (
        <>
            <AIButton
                onClick={handleButtonClick}
                loading={state.loading}
                loadingText="Extraction en cours..."
            >
                Importer une facture avec IA
            </AIButton>

            {!shopSelected && (
                <Alert severity="info" style={{ marginTop: '12px' }}>
                    Sélectionnez un magasin pour activer l'import de facture via l'IA.
                </Alert>
            )}

            {/* Upload Dialog with Drag & Drop */}
            <Dialog 
                open={state.uploadDialogOpen} 
                onClose={handleCancelUpload}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    📄 Importer une facture
                </DialogTitle>
                <DialogContent>
                    <Box {...getRootProps()} className={isDragActive ? `${classes.dropzone} ${classes.dropzoneActive}` : classes.dropzone}>
                        <input {...getInputProps()} />
                        
                        {state.file ? (
                            // Preview après sélection
                            <Box>
                                {state.preview ? (
                                    <img 
                                        src={state.preview} 
                                        alt="Preview" 
                                        style={{ 
                                            maxWidth: '100%', 
                                            maxHeight: '300px',
                                            marginBottom: '16px',
                                            borderRadius: '8px'
                                        }} 
                                    />
                                ) : (
                                    <Description style={{ fontSize: 64, color: '#667eea', marginBottom: '16px' }} />
                                )}
                                <Typography variant="body1" style={{ fontWeight: 600, marginBottom: '4px' }}>
                                    {state.file.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: '16px' }}>
                                    {(state.file.size / 1024).toFixed(2)} KB
                                </Typography>
                                <Typography variant="body2" color="primary" style={{ fontWeight: 500 }}>
                                    Cliquez ou glissez pour changer
                                </Typography>
                            </Box>
                        ) : (
                            // Zone de drop initiale
                            <Box>
                                {isDragActive ? (
                                    <>
                                        <CloudUpload style={{ fontSize: 64, color: '#667eea', marginBottom: '16px' }} />
                                        <Typography variant="h6" color="primary" style={{ fontWeight: 600 }}>
                                            Déposez la facture ici
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <ImageIcon style={{ fontSize: 64, color: '#667eea', marginBottom: '16px' }} />
                                        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '8px' }}>
                                            Glissez-déposez une facture
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                                            ou cliquez pour sélectionner
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Formats acceptés: JPG, PNG, PDF (max 10MB)
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>

                    {state.error && (
                        <Alert severity="error" style={{ marginTop: '16px' }}>
                            {state.error}
                        </Alert>
                    )}

                    {state.file && (
                        <Alert severity="info" style={{ marginTop: '16px' }}>
                            L'IA Mistral Pixtral va extraire automatiquement les produits, quantités, prix et informations de cette facture.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelUpload} color="default">
                        Annuler
                    </Button>
                    <Button 
                        onClick={handleExtract} 
                        color="primary" 
                        variant="contained"
                        disabled={!state.file || state.loading}
                        startIcon={state.loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                    >
                        {state.loading ? 'Extraction...' : 'Extraire les données'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Loading Overlay with steps */}
            {state.loading && (
                <Dialog open={state.loading}>
                    <DialogContent>
                        <Box display="flex" flexDirection="column" alignItems="center" p={3}>
                            <CircularProgress size={50} style={{ marginBottom: '24px' }} />
                            <Typography variant="h6" gutterBottom>
                                Traitement en cours...
                            </Typography>
                            <Box mt={2} width="100%" maxWidth={320}>
                                {LOADING_STEPS.map((step, i) => (
                                    <Box
                                        key={i}
                                        display="flex"
                                        alignItems="center"
                                        py={1}
                                        style={{
                                            opacity: i < state.loadingStep ? 0.7 : 1,
                                            fontWeight: i === state.loadingStep ? 600 : 400
                                        }}
                                    >
                                        {i < state.loadingStep ? (
                                            <CheckCircle style={{ color: '#4caf50', marginRight: 12, fontSize: 20 }} />
                                        ) : i === state.loadingStep ? (
                                            <CircularProgress size={20} style={{ marginRight: 12 }} />
                                        ) : (
                                            <Box width={20} height={20} style={{ marginRight: 12, opacity: 0.4 }} />
                                        )}
                                        <Typography variant="body2" color={i <= state.loadingStep ? "textPrimary" : "textSecondary"}>
                                            {step}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </DialogContent>
                </Dialog>
            )}

            {/* Extraction Results Dialog */}
            <Dialog 
                open={state.dialogOpen} 
                onClose={handleCancel}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
                        <CheckCircle style={{ color: '#4caf50' }} />
                        <Typography variant="h6">Données extraites</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {state.extractedData && (
                        <Box>
                            {/* Supplier Info + Devise détectée */}
                            <Box mb={2} p={2} bgcolor="#f5f5f5" borderRadius="8px">
                                    {state.extractedData.supplier && (
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Fournisseur:</strong> {state.extractedData.supplier}
                                        </Typography>
                                    )}
                                    {state.extractedData.invoice_number && (
                                        <Typography variant="body1" gutterBottom>
                                            <strong>N° Facture:</strong> {state.extractedData.invoice_number}
                                        </Typography>
                                    )}
                                    {state.extractedData.invoice_date && (
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Date:</strong> {state.extractedData.invoice_date}
                                        </Typography>
                                    )}
                                    <Typography variant="body1">
                                        <strong>Devise détectée par l'IA:</strong>{' '}
                                        {state.extractedData.currency === 'EUR'
                                            ? 'Euro (€)'
                                            : state.extractedData.currency === 'USD'
                                            ? 'Dollar ($)'
                                            : state.extractedData.currency === 'FCFA'
                                            ? 'FCFA'
                                            : 'Non détectée (FCFA par défaut)'}
                                    </Typography>
                                </Box>

                            {/* Products Table */}
                            <Box mb={1}>
                                <Typography variant="h6" gutterBottom>
                                    Produits ({state.extractedData.products?.length || 0})
                                </Typography>
                                {(() => {
                                    const unmatched = state.extractedData.products?.filter((p) => p.matched === false) || [];
                                    if (unmatched.length > 0) {
                                        return (
                                            <Alert severity="warning" style={{ marginTop: 8 }}>
                                                {unmatched.length} produit(s) non trouvé(s) dans le catalogue storehouse — affichés en rouge. 
                                                Vous pourrez les sélectionner manuellement après import.
                                            </Alert>
                                        );
                                    }
                                    return null;
                                })()}
                            </Box>
                            <TableContainer component={Paper} variant="outlined" style={{ marginBottom: '16px' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Produit</strong></TableCell>
                                            <TableCell><strong>Réf</strong></TableCell>
                                            <TableCell align="right"><strong>Qté</strong></TableCell>
                                            <TableCell align="right"><strong>Prix Unit.</strong></TableCell>
                                            <TableCell align="right"><strong>Total</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {state.extractedData.products?.map((product, index) => (
                                            <TableRow 
                                                key={index} 
                                                hover
                                                style={product.matched === false ? { backgroundColor: '#ffebee', color: '#c62828' } : undefined}
                                            >
                                                <TableCell>
                                                    {product.matched === false && (
                                                        <Typography component="span" style={{ color: '#c62828', fontWeight: 600, marginRight: 8 }}>
                                                            ⚠ Non trouvé
                                                        </Typography>
                                                    )}
                                                    {product.matched && product.matchedProduct
                                                        ? `${product.matchedProduct.marque} - ${product.matchedProduct.name}`
                                                        : product.name}
                                                </TableCell>
                                                <TableCell>{product.ref || '-'}</TableCell>
                                                <TableCell align="right">{product.quantity}</TableCell>
                                                <TableCell align="right">
                                                    <Currency value={product.unit_price} sourceCurrency={sourceCurrency} primaryFcfa={primaryFcfa} />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <strong><Currency value={product.total_price} sourceCurrency={sourceCurrency} primaryFcfa={primaryFcfa} /></strong>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Summary */}
                            <Box p={2} bgcolor="#f5f5f5" borderRadius="8px">
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="body1">Transport:</Typography>
                                    <Typography variant="body1">
                                        <Currency value={state.extractedData.transport || 0} sourceCurrency={sourceCurrency} primaryFcfa={primaryFcfa} />
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="body1">Taxes/Douane:</Typography>
                                    <Typography variant="body1">
                                        <Currency value={state.extractedData.taxes || 0} sourceCurrency={sourceCurrency} primaryFcfa={primaryFcfa} />
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop="2px solid #ddd">
                                    <Typography variant="h6">
                                        <strong>Total:</strong>
                                    </Typography>
                                    <Typography variant="h6" color="primary">
                                        <strong><Currency value={state.extractedData.total || 0} sourceCurrency={sourceCurrency} primaryFcfa={primaryFcfa} /></strong>
                                    </Typography>
                                </Box>
                            </Box>

                            <Alert severity="info" style={{ marginTop: '16px' }}>
                                Vérifiez les données extraites avant de les importer. 
                                Vous pourrez les modifier dans le formulaire.
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>
                        Annuler
                    </Button>
                    <Button 
                        onClick={handleConfirm} 
                        color="primary" 
                        variant="contained"
                        startIcon={<CheckCircle />}
                    >
                        Importer dans le formulaire
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default InvoiceUploader;
